import { useState } from 'react';
import { isAddress } from 'viem';
import { useAccount } from 'wagmi';
import spinnerData from '@/shared/animations/spinner-white.json';
import CodeBlock from '@/shared/components/atoms/CodeBlock';
import Button from '@/shared/components/flip-ui-kit/Button';
import MaxAmountInput from '@/shared/components/molecules/MaxAmountInput';
import { TextInput } from '@/shared/components/molecules/TextInput';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { type CastCacheValidatorFragment } from '@/shared/graphql/cache/fragments';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useTracking from '@/shared/hooks/useTracking';
import CoinsIcon from '@/shared/icons/flip-ui-kit/large/Coins';
import LockIcon from '@/shared/icons/flip-ui-kit/large/Lock';
import NextArrow from '@/shared/icons/NextArrow';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { noop } from '@/shared/utils/functions';
import Lottie from '@/shared/utils/Lottie';
import TokenAmount from '@/shared/utils/TokenAmount';
import ResumeDisclaimer from './ResumeDisclaimer';
import { getAllEnvironmentAddressesQuery } from '../../../../graphql/cache/environmentQueries';
import { useBigNumber } from '../../../../hooks/useBigNumber';
import { StakeEvents, type StakeTrackEvents } from '../../../../types/track';
import { BidPreview } from '../BidPreview';

type Step1Props = {
  validator: Omit<CastCacheValidatorFragment, 'totalBalance'> & { totalBalance: TokenAmount };
};

const Step1 = ({ validator }: Step1Props) => {
  const track = useTracking<StakeTrackEvents>();
  const { address } = useAccount();
  const { data } = useGqlQuery(getAllEnvironmentAddressesQuery, {
    context: { clientName: 'statechainCache' },
  });
  const redemptionTax = new TokenAmount(data?.allEnvironmentAddresses?.nodes[0].redemptionTax ?? 0);
  const [step, setStep] = useState<'enter' | 'execute'>('enter');
  const [inputAddress, setInputAddress] = useState(() => address ?? '');
  const [amount, setAmount, amountWei] = useBigNumber();
  const [addressFailureMessage, setAddressFailureMessage] = useState<string | undefined>(undefined);
  const [amountFailureMessage, setAmountFailureMessage] = useState<string | undefined>(undefined);
  const redeemAddress = validator.boundRedeemAddress ?? inputAddress;
  const redeemableBalance = validator.unlockedBalance.saturatingSub(redemptionTax);

  const code =
    amountWei === redeemableBalance.toBigInt()
      ? `chainflip-cli redeem ${redeemAddress}`
      : `chainflip-cli redeem --exact ${amount} ${redeemAddress}`;

  const validateAndProceed = () => {
    if (!redeemAddress || !isAddress(redeemAddress)) {
      return setAddressFailureMessage('The ethereum address is not valid');
    }
    setAddressFailureMessage(undefined);
    if (!amount?.length || !amountWei) {
      return setAmountFailureMessage('Redeem amount cannot be empty');
    }
    if (redeemableBalance.lt(amountWei.toString())) {
      return setAmountFailureMessage('Redeem amount must be lower than unlocked balance');
    }

    track(StakeEvents.CtaGenerateCliCommand, {
      props: {
        redeemAddress,
        connectedWallet: address as string,
        scAccount: validator.idSs58,
        path: window.location.pathname,
        position: 'redeem_modal',
        redeemAmount: amount,
      },
    });
    setAmountFailureMessage(undefined);
    return setStep('execute');
  };

  if (step === 'enter') {
    return (
      <>
        <div className="mb-8 ">
          <div className="text-24 text-cf-white">Redeem Funds</div>
          <div className="text-14">
            Redeeming {FLIP_SYMBOL} requires your Ethereum wallet and running a command in your
            validator CLI.
          </div>
        </div>
        <div className="mb-12 space-y-4">
          <MaxAmountInput
            label="Amount"
            placeholder="0.00"
            value={amount}
            message={amountFailureMessage}
            failure={Boolean(amountFailureMessage)}
            maxAmount={redeemableBalance}
            setAmountValue={setAmount}
            onKeyDown={(e) => {
              if (e.key === 'Enter') validateAndProceed();
            }}
          />
          <BidPreview
            className="border border-cf-gray-4"
            totalBalance={validator.totalBalance.sub(amountWei.toString())}
          />
          {validator.boundRedeemAddress ? (
            <TextInput
              label={
                <div className="flex items-center space-x-1.5">
                  <div>Bound ETH Address</div>
                  <QuestionMarkTooltip
                    content={
                      <div>
                        The account is bound to this redemption address and cannot be changed. Learn
                        more about{' '}
                        <a
                          className="underline"
                          target="_blank"
                          href="https://docs.chainflip.io/validators/mainnet/funding#advanced-account-management"
                          rel="noreferrer"
                        >
                          redeem address binding
                        </a>
                        .
                      </div>
                    }
                  />
                </div>
              }
              value={validator.boundRedeemAddress}
              startAdornment={<LockIcon className="h-[24px] w-[24px] text-cf-light-2" />}
              paddingLeft="pl-[36px]"
              onChange={noop}
              disabled
            />
          ) : (
            <TextInput
              label="ETH Address"
              placeholder="0x.."
              value={inputAddress}
              message={addressFailureMessage}
              failure={Boolean(addressFailureMessage)}
              onChange={(event) => setInputAddress(event.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') validateAndProceed();
              }}
            />
          )}
          <div className="flex flex-grow items-center space-x-1">
            <CoinsIcon className="h-[24px] w-[24px] text-cf-green-1" />
            <div className="grow text-16 text-cf-light-2">Redemption tax</div>
            <div className="text-14 text-cf-white">
              {redemptionTax.toFixed()} {FLIP_SYMBOL}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-start space-y-4 sm:flex-row sm:items-center sm:justify-between">
          <ResumeDisclaimer />
          <Button onClick={validateAndProceed} className="justify-center">
            <div className="flex items-center">
              Next
              <div className="ml-1 pt-[2px]">
                <NextArrow width="17" height="17" />
              </div>
            </div>
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <div className="text-24 text-cf-white">Execute Command</div>
        <div className="text-14">Log into your validator node and execute the command below.</div>
        <div className="font-aeonikMedium text-14 text-cf-white">
          Remember to change the path to the config file, which is specific to your system.
        </div>
      </div>
      <div className="space-y-4 pt-4">
        <div>Command</div>
        <CodeBlock code={code} />
        <div className="text-cf-light-1">
          You must execute this command in your Validator CLI. Remember to adjust the path of the
          config file to match your setup.
        </div>
        <div className="flex flex-col items-start space-y-2">
          <div className="flex items-center space-x-2">
            <Lottie
              as="div"
              animationData={spinnerData}
              autoplay
              loop
              className="h-[18px] w-[18px]"
            />
            <span className="text-14 text-cf-white">
              Waiting for command execution to continue...
            </span>
          </div>
          <ResumeDisclaimer />
        </div>
      </div>
    </>
  );
};

export default Step1;
